import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, mapTo, switchMap, tap } from 'rxjs/operators';
import { ICommonList } from 'src/app/core/services/data-providers/common.data-provider';
import { Company } from '../models/company.model';
import { User } from '../models/user.model';
import { CompaniesDataProvider } from './data-providers/companies.data-provider';
import { LoginService } from './login.service';
import { StorageService } from './storage.service';
import { PaginationMeta } from '../models/pagination.model';

@Injectable()
export class CompanyService {
  public CURRENT_USER!: User;

  private readonly companyId: BehaviorSubject<number>;
  public readonly companyId$: Observable<number>;

  private readonly companies: ReplaySubject<ICommonList<Company>> = new ReplaySubject<
    ICommonList<Company>
  >(1);
  public readonly companies$: Observable<ICommonList<Company>> = this.companies.asObservable();
  private readonly companiesCache: Map<number, Company> = new Map<number, Company>();

  private readonly currentCompany = new ReplaySubject<Company>(1);
  public readonly currentCompany$ = this.currentCompany.asObservable();

  private allCompanies: BehaviorSubject<boolean>;
  allCompanies$: Observable<boolean>;
  readonly all = Object.assign(new Company(), {
    id: undefined,
    title: 'All companies',
    hidden: false
  });

  public readonly updateCompaniesList$: Subject<void> = new Subject<void>();

  constructor(
    private readonly companiesDataProvider: CompaniesDataProvider,
    private readonly loginService: LoginService,
    private readonly storageService: StorageService
  ) {
    this.CURRENT_USER = this.storageService.getCurrentUser()!;
    this.allCompanies = new BehaviorSubject(false);
    this.allCompanies$ = this.allCompanies.asObservable();
    if (this.storageService.getCompanyId() === null) {
      this.storageService.setCompanyId(this.CURRENT_USER?.company?.id ?? 0);
    }
    const storedId = this.storageService.getCompanyId() ?? 0;
    this.companyId = new BehaviorSubject<number>(storedId);
    this.companyId$ = this.companyId.asObservable();
    this.subscribeEmitters();
    this.requestUpdateCompaniesList();
  }

  setAllCompanies(allCompanies: boolean) {
    this.allCompanies.next(allCompanies);
  }

  public get COMPANY_ID() {
    return this.companyId.getValue();
  }

  public requestUpdateCompaniesList(): void {
    this.updateCompaniesList$.next();
  }

  public getCompaniesList(): Observable<ICommonList<Company>> {
    return this.companies;
  }

  public setCompanyId(id: number): void {
    this.companyId.next(id);
  }

  public setCurrentCompany(company: Company): void {
    this.currentCompany.next(company);
  }

  public getCompanyById(id: number): Company | undefined {
    return this.companiesCache.get(id);
  }

  public requestCompanyById(id: number): void {
    this.companiesDataProvider.getCompany(id).subscribe((company: Company) => {
      this.companiesCache.set(company.id, company);
      if (this.companyId.getValue() === id) {
        this.setCompanyId(id);
      }
    });
  }

  private subscribeEmitters(): void {
    combineLatest([
      this.companyId.pipe(
        distinctUntilChanged(),
        switchMap((id: number) => {
          if (id > 0) {
            return this.companiesCache.has(id)
              ? of(this.companiesCache.get(id) as Company)
              : this.companiesDataProvider.getCompany(id);
          }
          return of(this.all);
        })
      ),
      this.allCompanies$
    ]).subscribe(([company, allCompanies]) => {
      const c = company as Company;
      this.requestUpdateCompaniesList();
      if (allCompanies && c.id == null) {
        this.currentCompany.next(c);
      } else {
        if (!allCompanies && c.id == null) {
          return this.companyId.next(this.storageService.getCompanyId() ?? 0);
        }
        this.currentCompany.next(c);
        this.companiesCache.set(c.id!, c);
        this.requestUpdateCompaniesList();
        this.storageService.setCompanyId(c.id!);
      }
    });

    this.updateCompaniesList$
      .pipe(switchMap(() => this.companiesDataProvider.getCompanies()))
      .subscribe((companyList: ICommonList<Company>) => {
        if (this.storageService.getCompanyId() === 0) {
          this.storageService.setCompanyId(companyList.items[0].id);
          this.companyId.next(companyList.items[0].id);
        }

        this.companies.next(companyList);
        companyList.items.forEach((company: Company) => {
          this.companiesCache.set(company.id, company);
        });
      });

    this.loginService
      .getUserChangedSubject()
      .pipe(switchMap(() => this.companiesDataProvider.getCompanies()))
      .subscribe(companies => {
        this.setCompanyId(companies.items[0].id);
        this.companies.next(companies);
      });
  }

  getCompaniesPaged(pagination: PaginationMeta, filter = '') {
    return this.companiesDataProvider.getCompaniesPaged(pagination, filter);
  }
}
